import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Collapse,
  Snackbar
} from '@mui/material';
import { Block as BlockIcon, Gavel as GavelIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, VerifiedUser as WhitelistIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import api from '../api';

const AuctionFarmers = () => {
  const [farmers, setFarmers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  
  // Settings
  const [daysLookback, setDaysLookback] = useState(60);
  const [amountThreshold, setAmountThreshold] = useState(10000);
  const [showBanned, setShowBanned] = useState(true);

  const fetchFarmers = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await api.get('/analytics/auction-farmers', {
        params: {
          days: daysLookback,
          threshold: amountThreshold,
          showBanned: showBanned
        }
      });
      setFarmers(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch auction farmers data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFarmers();
  }, []);

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const handleBanUser = async (userId, isPermanent = false) => {
    try {
      const response = await api.post(`/users/${userId}/ban`, {
        isPermanent,
        reason: `Auction house farming - flagged from analysis`
      });
      
      // Update local state
      setFarmers(prev => prev.map(farmer => 
        farmer.userId === userId 
          ? { ...farmer, isBanned: true }
          : farmer
      ));
      
      setToast({
        open: true,
        message: response.data.message || 'User banned successfully',
        severity: 'success'
      });
    } catch (err) {
      setToast({
        open: true,
        message: err.response?.data?.error || 'Failed to ban user',
        severity: 'error'
      });
    }
  };

  const handleWhitelistUser = async (userId) => {
    try {
      const response = await api.post(`/users/${userId}/whitelist`, {
        reason: `Whitelisted from auction farmers analysis`
      });
      
      // Update local state
      setFarmers(prev => prev.map(farmer => 
        farmer.userId === userId 
          ? { ...farmer, whitelisted: true }
          : farmer
      ));
      
      setToast({
        open: true,
        message: response.data.message || 'User whitelisted successfully',
        severity: 'success'
      });
    } catch (err) {
      setToast({
        open: true,
        message: err.response?.data?.error || 'Failed to whitelist user',
        severity: 'error'
      });
    }
  };

  const toggleRowExpansion = (userId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ color: '#fff' }}>
        Auction House Farmers Analysis
      </Typography>
      
      <Paper sx={{ p: 3, mb: 3, bgcolor: '#1e1e1e', color: '#fff' }}>
        <Typography variant="h6" gutterBottom>
          Analysis Settings
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', mb: 2 }}>
          <TextField
            label="Days to Look Back"
            type="number"
            value={daysLookback}
            onChange={(e) => setDaysLookback(parseInt(e.target.value) || 60)}
            sx={{ 
              width: 150,
              '& .MuiInputBase-input': { color: '#fff' },
              '& .MuiInputLabel-root': { color: '#aaa' },
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#555' },
                '&:hover fieldset': { borderColor: '#777' },
                '&.Mui-focused fieldset': { borderColor: '#90caf9' }
              }
            }}
          />
          
          <TextField
            label="Amount Threshold"
            type="number"
            value={amountThreshold}
            onChange={(e) => setAmountThreshold(parseInt(e.target.value) || 10000)}
            sx={{ 
              width: 150,
              '& .MuiInputBase-input': { color: '#fff' },
              '& .MuiInputLabel-root': { color: '#aaa' },
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#555' },
                '&:hover fieldset': { borderColor: '#777' },
                '&.Mui-focused fieldset': { borderColor: '#90caf9' }
              }
            }}
          />
          
          <FormControlLabel
            control={
              <Switch
                checked={showBanned}
                onChange={(e) => setShowBanned(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#90caf9',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#90caf9',
                  },
                }}
              />
            }
            label="Show Banned Users"
            sx={{ color: '#fff' }}
          />
          
          <Button
            variant="contained"
            onClick={fetchFarmers}
            disabled={loading}
            sx={{ 
              bgcolor: '#90caf9', 
              color: '#000',
              '&:hover': { bgcolor: '#64b5f6' }
            }}
          >
            {loading ? <CircularProgress size={20} /> : 'Analyze'}
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3, bgcolor: '#d32f2f', color: '#fff' }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ bgcolor: '#1e1e1e', color: '#fff' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: '#2d2d2d', color: '#fff', fontWeight: 'bold' } }}>
                <TableCell>User ID</TableCell>
                <TableCell>Username</TableCell>
                <TableCell>Email</TableCell>
                <TableCell align="right">Total Auction Income</TableCell>
                <TableCell align="center">Transaction Count</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Risk Level</TableCell>
                <TableCell align="center">Top Buyers</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {farmers.map((farmer) => (
                <TableRow 
                  key={farmer.userId}
                  sx={{ 
                    '& td': { color: '#fff', borderColor: '#333' },
                    '&:hover': { bgcolor: '#2d2d2d' }
                  }}
                >
                  <TableCell>
                    <Link 
                      to={`/user/${farmer.userId}`}
                      style={{ color: '#90caf9', textDecoration: 'none' }}
                    >
                      {farmer.userId}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link 
                      to={`/user/${farmer.userId}`}
                      style={{ color: '#90caf9', textDecoration: 'none' }}
                    >
                      {farmer.username || 'N/A'}
                    </Link>
                  </TableCell>
                  <TableCell>{farmer.email || 'N/A'}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#90caf9' }}>
                      {formatAmount(farmer.totalIncome)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">{farmer.transactionCount}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={farmer.isBanned ? 'BANNED' : 'ACTIVE'}
                      color={farmer.isBanned ? 'error' : 'success'}
                      size="small"
                      sx={{
                        bgcolor: farmer.isBanned ? '#d32f2f' : '#2e7d32',
                        color: '#fff'
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={farmer.riskLevel}
                      color={
                        farmer.riskLevel === 'HIGH' ? 'error' :
                        farmer.riskLevel === 'MEDIUM' ? 'warning' : 'info'
                      }
                      size="small"
                      sx={{
                        bgcolor: 
                          farmer.riskLevel === 'HIGH' ? '#d32f2f' :
                          farmer.riskLevel === 'MEDIUM' ? '#f57c00' : '#1976d2',
                        color: '#fff'
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {farmer.topBuyers?.slice(0, 5).map((buyer, index) => (
                        <Box key={buyer.buyerId} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" sx={{ color: '#90caf9', minWidth: '20px' }}>
                            #{index + 1}
                          </Typography>
                          <Link 
                            to={`/user/${buyer.buyerId}`}
                            style={{ textDecoration: 'none' }}
                          >
                            <Typography variant="caption" sx={{ color: '#90caf9' }}>
                              {buyer.buyerUsername || `ID: ${buyer.buyerId}`}
                            </Typography>
                          </Link>
                          <Typography variant="caption" sx={{ color: '#aaa' }}>
                            ({formatAmount(buyer.totalSpent)})
                          </Typography>
                        </Box>
                      ))}
                      {(!farmer.topBuyers || farmer.topBuyers.length === 0) && (
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          No buyers
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    {!farmer.isBanned && !farmer.whitelisted && (
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Tooltip title="Whitelist User">
                          <IconButton
                            size="small"
                            onClick={() => handleWhitelistUser(farmer.userId)}
                            sx={{ color: '#4caf50' }}
                          >
                            <WhitelistIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Temporary Ban">
                          <IconButton
                            size="small"
                            onClick={() => handleBanUser(farmer.userId, false)}
                            sx={{ color: '#ff9800' }}
                          >
                            <BlockIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Permanent Ban">
                          <IconButton
                            size="small"
                            onClick={() => handleBanUser(farmer.userId, true)}
                            sx={{ color: '#d32f2f' }}
                          >
                            <GavelIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                    {farmer.isBanned && (
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        Already banned
                      </Typography>
                    )}
                    {farmer.whitelisted && (
                      <Typography variant="caption" sx={{ color: '#4caf50' }}>
                        Whitelisted
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        {farmers.length === 0 && !loading && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" sx={{ color: '#aaa' }}>
              No auction house farmers found with current criteria
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Toast Notification */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setToast({ ...toast, open: false })} 
          severity={toast.severity}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AuctionFarmers;
